import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useLang } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Compass, House, FilePlus, Globe, UserCircle, SignOut, ShieldCheck, HardHat } from "@phosphor-icons/react";

const ROLE_META = {
  admin: { label: "Admin", Icon: ShieldCheck, color: "text-orange-500" },
  operativo: { label: "Operativo", Icon: UserCircle, color: "text-blue-500" },
  campo: { label: "Campo", Icon: HardHat, color: "text-emerald-500" },
};

export default function Layout() {
  const { t, lang, setLang } = useLang();
  const { role, logout, can } = useAuth();
  const navigate = useNavigate();
  const RoleIcon = role ? ROLE_META[role].Icon : UserCircle;

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-slate-50 topo-bg">
      {/* Sidebar */}
      <aside className="hidden md:flex w-64 flex-col border-r border-slate-200 bg-white">
        <div className="px-6 py-6 border-b border-slate-200">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 bg-slate-900 flex items-center justify-center rounded-sm">
              <Compass weight="duotone" size={22} color="#f97316" />
            </div>
            <div>
              <div className="font-black text-base tracking-tight text-slate-900">{t.app_name}</div>
              <div className="coord-label">DOM • CRM</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          <NavLink
            to="/"
            data-testid="nav-dashboard"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 text-sm rounded-sm transition-colors ${
                isActive ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"
              }`
            }
            end
          >
            <House weight="duotone" size={18} />
            {t.nav_dashboard}
          </NavLink>
          {can("createExpediente") && (
            <NavLink
              to="/nuevo"
              data-testid="nav-new"
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 text-sm rounded-sm transition-colors ${
                  isActive ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"
                }`
              }
            >
              <FilePlus weight="duotone" size={18} />
              {t.nav_new}
            </NavLink>
          )}
          {can("editProfile") && (
            <NavLink
              to="/perfil"
              data-testid="nav-profile"
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 text-sm rounded-sm transition-colors ${
                  isActive ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"
                }`
              }
            >
              <UserCircle weight="duotone" size={18} />
              {t.nav_profile}
            </NavLink>
          )}
        </nav>
        {role && (
          <div className="px-3 py-3 border-t border-slate-200">
            <div className="coord-label mb-1.5 px-1">// SESIÓN</div>
            <div className="flex items-center gap-2 px-1 mb-2">
              <RoleIcon weight="duotone" size={16} className={ROLE_META[role].color} />
              <span className="text-xs font-bold text-slate-900" data-testid="role-badge">{ROLE_META[role].label}</span>
            </div>
            <Button
              data-testid="logout-btn"
              size="sm"
              variant="outline"
              onClick={handleLogout}
              className="w-full rounded-sm text-xs"
            >
              <SignOut size={12} className="mr-1.5" />Cerrar sesión
            </Button>
          </div>
        )}
        <div className="p-3 border-t border-slate-200">
          <div className="coord-label mb-2 px-1">Language</div>
          <div className="flex gap-1">
            <Button
              data-testid="lang-es-btn"
              size="sm"
              variant={lang === "es" ? "default" : "outline"}
              onClick={() => setLang("es")}
              className="flex-1 rounded-sm font-mono text-xs"
            >ES</Button>
            <Button
              data-testid="lang-en-btn"
              size="sm"
              variant={lang === "en" ? "default" : "outline"}
              onClick={() => setLang("en")}
              className="flex-1 rounded-sm font-mono text-xs"
            >EN</Button>
          </div>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-30 bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-8 h-8 bg-slate-900 flex items-center justify-center rounded-sm">
            <Compass weight="duotone" size={18} color="#f97316" />
          </div>
          <span className="font-black tracking-tight">{t.app_name}</span>
        </Link>
        <div className="flex items-center gap-1">
          <Button
            data-testid="lang-toggle-mobile"
            size="sm"
            variant="outline"
            onClick={() => setLang(lang === "es" ? "en" : "es")}
            className="rounded-sm font-mono text-xs"
          >
            <Globe size={14} className="mr-1" />
            {lang.toUpperCase()}
          </Button>
        </div>
      </div>

      <main className="flex-1 md:ml-0 mt-14 md:mt-0 min-w-0">
        <Outlet />
      </main>
    </div>
  );
}
